import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';

import type { ApiError } from '../../api/client';
import {
    useAddComment,
    useApproveCase,
    useAssignCase,
    useCase,
    useDecideExtension,
    useEscalateCase,
    useExtendCase,
    useFollowup,
    usePendingTurn,
    useGenerateDraft,
    usePatchCase,
    useReopenCase,
    useReviewCase,
    useSaveDraft,
    useSendCase,
    useSplitOutMessage,
    useToApproval,
    useToggleCheck,
    useUndoMessage,
} from '../../api/hooks';
import type { FollowupRequest, Verdict } from '../../api/types';
import { useAuth } from '../../auth/AuthContext';
import { fmtDate } from '../../lib/format';
import { useModalLayer } from '../Modal';
import { useToast } from '../Toast';

import ActionBar from './ActionBar';
import CaseHeader from './CaseHeader';
import StageTrack from './StageTrack';
import TabCard from './TabCard';
import TabDraft from './TabDraft';
import TabHistory from './TabHistory';
import TabThread from './TabThread';
import { AssignCaseModal, ExtendCaseModal, SendCaseModal } from './SubModals';
import { LoadingState } from '../ErrorState';

export type CaseTab = 'thread' | 'draft' | 'card' | 'history';

/** Вкладки в фиксированном порядке (ui-case-card.md §6.1). */
const TABS: { key: CaseTab; label: string }[] = [
    { key: 'thread', label: 'Разбор обращения' },
    { key: 'draft', label: 'Проект ответа' },
    { key: 'card', label: 'Карточка дела' },
    { key: 'history', label: 'История' },
];

const TITLE_ID = 'gmsCaseModalTitle';

/** Ход, который уже отправлен куратору, но ответа ещё нет. */
export interface PendingTurn {
    text: string;
    fileName: string | null;
}

export interface CaseCardModalProps {
    /** Идентификатор дела: параметр маршрута `/cases/:id`. */
    caseId: string;
    /** Закрытие модалки. По умолчанию — шаг назад по истории браузера. */
    onClose?: () => void;
    /** Переход на связанное дело. Без него дело открывается внутри этой же модалки. */
    onOpenCase?: (caseId: string) => void;
    /** Возврат в выборку, из которой карточка открыта. */
    onBack?: () => void;
    backLabel?: string;
}

/**
 * Карточка дела — модалка поверх списка (ТЗ §8.5).
 * Порядок блоков жёсткий: шапка → действия → стадии → вкладки → тело.
 */
/** Шапка сворачивается после стольких пикселей прокрутки тела и только если прокрутки больше, чем она освобождает. */
const COMPACT_AFTER_PX = 48;
const COMPACT_MIN_ROOM_PX = 260;

export function CaseCardModal({ caseId, onClose, onOpenCase, onBack, backLabel }: CaseCardModalProps) {
    const toast = useToast();
    const { role, user } = useAuth();

    const [stack, setStack] = useState<string[]>([]);
    const activeId = stack.length > 0 ? stack[stack.length - 1] : caseId;

    const [tab, setTab] = useState<CaseTab>('thread');
    const [sub, setSub] = useState<null | 'send' | 'assign' | 'extend'>(null);
    const [commentFocusSeq, setCommentFocusSeq] = useState(0);
    const [pendingTurn, setPendingTurn] = useState<PendingTurn | null>(null);
    // Компактная шапка (доработка шага 11): при прокрутке тела шапка сворачивается в одну строку, стадии прячутся.
    const [compact, setCompact] = useState(false);

    const overlayRef = useRef<HTMLDivElement | null>(null);
    const subOverlayRef = useRef<HTMLDivElement | null>(null);
    const bodyRef = useRef<HTMLDivElement | null>(null);
    const closeBtnRef = useRef<HTMLButtonElement | null>(null);

    const caseQuery = useCase(activeId);
    const detail = caseQuery.data ?? null;

    const approve = useApproveCase(activeId);
    const send = useSendCase(activeId);
    const extend = useExtendCase(activeId);
    const decideExtension = useDecideExtension(activeId);
    const assign = useAssignCase(activeId);
    const escalate = useEscalateCase(activeId);
    const reopen = useReopenCase(activeId);
    const patchCase = usePatchCase(activeId);
    const addComment = useAddComment(activeId);
    const toggleCheck = useToggleCheck(activeId);
    const reviewTurn = useReviewCase(activeId);
    const undoTurn = useUndoMessage(activeId);
    const splitOut = useSplitOutMessage(activeId);
    const generateDraft = useGenerateDraft(activeId);
    const saveDraft = useSaveDraft(activeId);
    const toApproval = useToApproval(activeId);
    const followup = useFollowup(activeId);
    // Ход кодового движка, начатый до перезагрузки или в другой вкладке: опрос до ответа.
    const remoteTurn = !followup.isPending ? (detail?.pending_turn ?? null) : null;
    const remotePending = usePendingTurn(activeId, remoteTurn ? remoteTurn.job_id : null);

    // Ход к куратору идёт до 180 с и не должен блокировать остальные кнопки.
    const busy =
        approve.isPending ||
        send.isPending ||
        extend.isPending ||
        decideExtension.isPending ||
        assign.isPending ||
        escalate.isPending ||
        reopen.isPending ||
        patchCase.isPending ||
        addComment.isPending ||
        toggleCheck.isPending ||
        reviewTurn.isPending ||
        undoTurn.isPending ||
        splitOut.isPending ||
        generateDraft.isPending ||
        saveDraft.isPending ||
        toApproval.isPending;

    const fail = useCallback((error: ApiError) => toast(error.message), [toast]);

    /* ---- навигация ---- */

    useEffect(() => {
        setStack([]);
    }, [caseId]);

    // При открытии другого дела вкладка всегда сбрасывается на «Разбор обращения».
    useEffect(() => {
        setTab('thread');
        setSub(null);
        setPendingTurn(null);
    }, [activeId]);

    useEffect(() => {
        if (bodyRef.current) bodyRef.current.scrollTop = 0;
        setCompact(false);
    }, [tab, activeId]);

    /** Свернуть шапку, когда тело прокручено вниз и прокручивать есть что; развернуть — у самого верха (с запасом,
        чтобы шапка не мигала: свёрнутая шапка ниже, тело выше, и прокрутка должна оставаться). */
    const onBodyScroll = useCallback(() => {
        const el = bodyRef.current;
        if (!el) return;
        const room = el.scrollHeight - el.clientHeight;
        setCompact((now) => (now ? el.scrollTop > 4 : el.scrollTop > COMPACT_AFTER_PX && room > COMPACT_MIN_ROOM_PX));
    }, []);

    const close = useCallback(() => {
        if (onClose) onClose();
        else window.history.back();
    }, [onClose]);

    const openRelated = useCallback(
        (id: string) => {
            if (onOpenCase) onOpenCase(id);
            else setStack((prev) => [...prev, id]);
        },
        [onOpenCase],
    );

    const goBack = useCallback(() => {
        if (stack.length > 0) setStack((prev) => prev.slice(0, -1));
        else if (onBack) onBack();
    }, [stack.length, onBack]);

    const backVisible = stack.length > 0 || Boolean(onBack);
    const backText = stack.length > 0 ? '← Назад к делу' : (backLabel ?? '← Назад к списку');

    /* ---- фон: inert и блокировка прокрутки (разрыв ui-case-card.md §3.6) ---- */

    /* Счётчик открытых окон общий с `Modal`: карточка часто открыта поверх модалки
       со списком дел, и снимать `inert` можно только когда закрылось последнее окно.
       Он же говорит, нужен ли оверлею класс `nested` — иначе порядок карточки и
       списка держался бы на порядке порталов при равном z-index. */
    const nested = useModalLayer();

    useEffect(() => {
        if (closeBtnRef.current) closeBtnRef.current.focus();
    }, []);

    /* ---- Escape и ловушка фокуса (ui-case-card.md §3.5) ---- */

    useEffect(() => {
        function onKeyDown(event: KeyboardEvent) {
            const root = subOverlayRef.current ?? overlayRef.current;
            if (!root) return;
            if (event.key === 'Escape') {
                if (sub) setSub(null);
                else close();
                return;
            }
            if (event.key !== 'Tab') return;
            const all = root.querySelectorAll<HTMLElement>(
                'button:not([disabled]), select, textarea, input, a[href], [tabindex]:not([tabindex="-1"])',
            );
            // Скрытые вкладки остаются в DOM: элементы без геометрии из ловушки убираем.
            const focusable: HTMLElement[] = [];
            all.forEach((node) => {
                if (node.getClientRects().length > 0) focusable.push(node);
            });
            if (focusable.length === 0) return;
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            const active = document.activeElement as HTMLElement | null;
            if (event.shiftKey && active === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && active === last) {
                event.preventDefault();
                first.focus();
            } else if (!active || focusable.indexOf(active) === -1) {
                event.preventDefault();
                first.focus();
            }
        }
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [sub, close]);

    /* ---- действия ---- */

    const handleApprove = () =>
        approve.mutate(undefined, {
            onSuccess: () => toast('Ответ согласован. Отправка — следующим действием.'),
            onError: fail,
        });

    const handleSend = (justified: boolean) => {
        setSub(null);
        send.mutate(
            { justified },
            { onSuccess: () => toast('Ответ отправлен заявителю, дело закрыто'), onError: fail },
        );
    };

    const handleExtend = (reason: string, comment: string) => {
        setSub(null);
        extend.mutate(comment ? { reason, comment } : { reason }, {
            onSuccess: (data) =>
                toast('Срок продлён до ' + (fmtDate(data.case.legal_deadline) || 'нового срока')),
            onError: fail,
        });
    };

    const handleExtendDecide = (approved: boolean) =>
        decideExtension.mutate(
            { approved },
            {
                onSuccess: () => toast(approved ? 'Продление утверждено' : 'Продление отклонено, срок возвращён'),
                onError: fail,
            },
        );

    const handleAssign = (executorId: string | null, executorName: string) => {
        setSub(null);
        assign.mutate(
            { executor_id: executorId },
            {
                onSuccess: () => toast(executorId ? 'Дело передано: ' + executorName : 'Исполнитель снят'),
                onError: fail,
            },
        );
    };

    const handleEscalate = () =>
        escalate.mutate(
            {},
            { onSuccess: () => toast('Дело добавлено в «Ждут вашего решения»'), onError: fail },
        );

    const handleReopen = () =>
        reopen.mutate({}, { onSuccess: () => toast('Дело переоткрыто'), onError: fail });

    const handleChangeDepartment = (departmentId: string | null, name: string) =>
        patchCase.mutate(
            { department_id: departmentId },
            {
                onSuccess: () => toast(departmentId ? 'Отделение изменено: ' + name : 'Отделение снято'),
                onError: fail,
            },
        );

    const handleReclassify = (category: string) =>
        patchCase.mutate(
            { primary_category: category },
            { onSuccess: () => toast('Категория изменена: ' + category), onError: fail },
        );

    const handleComment = (text: string): boolean => {
        const value = text.trim();
        if (!value) {
            toast('Комментарий пустой');
            return false;
        }
        addComment.mutate({ text: value }, { onSuccess: () => toast('Комментарий добавлен'), onError: fail });
        return true;
    };

    const handleCheck = (checkId: string, done: boolean) =>
        toggleCheck.mutate({ check_id: checkId, done }, { onError: fail });

    const handleReview = (verdict: Verdict, comment: string, messageId: number | null) =>
        reviewTurn.mutate(
            {
                verdict,
                ...(comment ? { comment } : {}),
                ...(messageId !== null && messageId > 0 ? { message_id: messageId } : {}),
            },
            {
                onSuccess: () =>
                    toast(verdict === 'ok' ? 'Оценка сохранена: замечаний нет' : 'Оценка сохранена: есть замечания'),
                onError: fail,
            },
        );

    const handleUndo = (messageId: number) =>
        undoTurn.mutate(messageId, {
            onSuccess: () => toast('Ход отменён, чек-лист восстановлен'),
            onError: fail,
        });

    const handleSplitOut = (messageId: number) =>
        splitOut.mutate(messageId, {
            onSuccess: () => toast('Обращение вынесено в отдельное дело'),
            onError: fail,
        });

    const handleGenerateDraft = (again: boolean) =>
        generateDraft.mutate(undefined, {
            onSuccess: () => toast(again ? 'Проект ответа сформирован заново' : 'Проект ответа сформирован'),
            onError: fail,
        });

    const handleSaveDraft = (text: string) =>
        saveDraft.mutate({ text }, { onSuccess: () => toast('Черновик сохранён'), onError: fail });

    const handleToApproval = () =>
        toApproval.mutate(undefined, {
            onSuccess: () => toast('Проект отправлен на согласование'),
            onError: fail,
        });

    const handleGoComment = () => {
        setTab('history');
        setCommentFocusSeq((n) => n + 1);
    };

    const sendTurn = useCallback(
        async (input: FollowupRequest, fileName: string | null) => {
            setPendingTurn({ text: input.message, fileName });
            try {
                const data = await followup.mutateAsync(input);
                if (data.turn && data.turn.reclassify_suggested) {
                    toast('Куратор предлагает переквалифицировать дело');
                }
            } catch (error) {
                toast((error as ApiError).message);
                throw error;
            } finally {
                setPendingTurn(null);
            }
        },
        [followup, toast],
    );

    const caseItem = detail ? detail.case : null;
    const loadError = caseQuery.isError ? caseQuery.error.message : null;

    const overlay = (
        <div
            className={nested ? 'modal-overlay nested show' : 'modal-overlay show'}
            role="dialog"
            aria-modal="true"
            aria-labelledby={TITLE_ID}
            ref={overlayRef}
            onMouseDown={(event) => {
                if (event.target === event.currentTarget) close();
            }}
        >
            <div className={compact ? 'modal modal-case compact' : 'modal modal-case'}>
                <CaseHeader
                    titleId={TITLE_ID}
                    caseItem={caseItem}
                    title={caseItem ? caseItem.title : 'Карточка дела'}
                    loading={caseQuery.isPending}
                    error={loadError}
                    backVisible={backVisible}
                    backText={backText}
                    onBack={goBack}
                    onClose={close}
                    closeRef={closeBtnRef}
                />

                {detail ? (
                    <ActionBar
                        detail={detail}
                        busy={busy}
                        onApprove={handleApprove}
                        onSend={() => setSub('send')}
                        onExtend={() => setSub('extend')}
                        onExtendDecide={handleExtendDecide}
                        onAssign={() => setSub('assign')}
                        onEscalate={handleEscalate}
                        onComment={handleGoComment}
                        onReopen={handleReopen}
                        onChangeDepartment={handleChangeDepartment}
                    />
                ) : null}

                {caseItem ? <StageTrack stage={caseItem.stage} /> : null}

                <div className="modal-tabs" role="tablist" aria-label="Разделы карточки дела">
                    {detail
                        ? TABS.map((item) => (
                              <button
                                  key={item.key}
                                  type="button"
                                  role="tab"
                                  id={'gmsCaseTab-' + item.key}
                                  aria-selected={tab === item.key}
                                  aria-controls={'gmsCasePane-' + item.key}
                                  className={tab === item.key ? 'active' : undefined}
                                  onClick={() => setTab(item.key)}
                              >
                                  {item.label}
                              </button>
                          ))
                        : null}
                </div>

                <div className="modal-body" ref={bodyRef} onScroll={onBodyScroll}>
                    {caseQuery.isPending ? <LoadingState variant="inline" label="Загружаем дело…" /> : null}
                    {loadError ? <div className="list-empty">{loadError}</div> : null}

                    {detail ? (
                        <>
                            <Pane tab="thread" active={tab}>
                                <TabThread
                                    detail={detail}
                                    userName={user ? user.full_name : null}
                                    busy={busy}
                                    pending={followup.isPending || remotePending}
                                    pendingTurn={
                                        pendingTurn ??
                                        (remotePending && remoteTurn
                                            ? { text: remoteTurn.message, fileName: remoteTurn.has_file ? 'вложение' : null }
                                            : null)
                                    }
                                    onCheck={handleCheck}
                                    onReview={handleReview}
                                    onUndo={handleUndo}
                                    onSplitOut={handleSplitOut}
                                    onReclassify={handleReclassify}
                                    onSendTurn={sendTurn}
                                    onNotice={toast}
                                />
                            </Pane>
                            <Pane tab="draft" active={tab}>
                                <TabDraft
                                    detail={detail}
                                    busy={busy}
                                    onGenerate={handleGenerateDraft}
                                    onSave={handleSaveDraft}
                                    onToApproval={handleToApproval}
                                    onSend={() => setSub('send')}
                                />
                            </Pane>
                            <Pane tab="card" active={tab}>
                                <TabCard detail={detail} onOpenCase={openRelated} />
                            </Pane>
                            <Pane tab="history" active={tab}>
                                <TabHistory
                                    detail={detail}
                                    currentUserId={user ? user.id : null}
                                    busy={busy}
                                    focusSeq={commentFocusSeq}
                                    onComment={handleComment}
                                />
                            </Pane>
                        </>
                    ) : null}
                </div>
            </div>
        </div>
    );

    return createPortal(
        <>
            {overlay}

            {detail && sub === 'send' ? (
                <SendCaseModal
                    detail={detail}
                    busy={busy}
                    overlayRef={subOverlayRef}
                    onCancel={() => setSub(null)}
                    onConfirm={handleSend}
                />
            ) : null}

            {detail && sub === 'assign' ? (
                <AssignCaseModal
                    detail={detail}
                    busy={busy}
                    overlayRef={subOverlayRef}
                    onCancel={() => setSub(null)}
                    onConfirm={handleAssign}
                />
            ) : null}

            {detail && sub === 'extend' ? (
                <ExtendCaseModal
                    detail={detail}
                    role={role}
                    busy={busy}
                    overlayRef={subOverlayRef}
                    onCancel={() => setSub(null)}
                    onConfirm={handleExtend}
                />
            ) : null}
        </>,
        document.body,
    );
}

/** `.tab-pane` собственного CSS не имеет: скрытие делает атрибут `hidden`. */
function Pane({ tab, active, children }: { tab: CaseTab; active: CaseTab; children: ReactNode }) {
    return (
        <div
            className="tab-pane"
            id={'gmsCasePane-' + tab}
            role="tabpanel"
            aria-labelledby={'gmsCaseTab-' + tab}
            hidden={active !== tab}
        >
            {children}
        </div>
    );
}

export default CaseCardModal;
